import React, { useEffect, useState } from 'react';
import { useDashboardData } from '../data/DashboardDataProvider';
import ChartCard from '../components/chart/ChartCard';
import { getFirstProgramStartsPerWeekURL, http } from '../services/http';
import { getDataSet } from '../utils/dataManager';
import { colors } from '../utils/colors';

const WEEKS_TO_SKIP = 4;
const WEEKS_TO_USE = 4;

function formatWeek(ts) {
  const date = new Date(parseInt(ts, 10) * 1000);
  return date.toISOString().slice(0, 10);
}

function getWindowedWeeks(data) {
  // Sort weeks descending, skip the most recent 2, take the next 4
  const sorted = [...data]
    .filter(row => row.registration_week)
    .sort((a, b) => parseInt(b.registration_week, 10) - parseInt(a.registration_week, 10));
  return sorted.slice(WEEKS_TO_SKIP, WEEKS_TO_SKIP + WEEKS_TO_USE);
}

const OnboardingFunnel = () => {
  const { analyticsData, errors } = useDashboardData();
  const { analyticsEventsPerDay } = analyticsData;

  const [segments, setSegments] = useState({ loading: true, data: [] });
  const [firstProgramStarts, setFirstProgramStarts] = useState({ loading: true, data: [] });
  const [segError, setSegError] = useState(null);
  const [startsError, setStartsError] = useState(null);

  useEffect(() => {
    http.get('/admin/stats/view/materialized_user_segments_per_registration_week')
      .then(res => setSegments({ loading: false, data: res.data }))
      .catch(() => { setSegments({ loading: false, data: [] }); setSegError('Failed to load user segments'); });

    http.get(getFirstProgramStartsPerWeekURL())
      .then(res => setFirstProgramStarts({ loading: false, data: res.data }))
      .catch(() => { setFirstProgramStarts({ loading: false, data: [] }); setStartsError('Failed to load first program starts'); });
  }, []);

  const loading = analyticsEventsPerDay.loading || segments.loading || firstProgramStarts.loading;

  // User segments - skip 2 most recent weeks, use 4 before that
  const windowedSegments = getWindowedWeeks(segments.data);
  const selectedWeekKeys = new Set(windowedSegments.map(row => String(row.registration_week)));

  // Derive date range from actual segment weeks for web downloads
  const weekTimestamps = windowedSegments.map(row => parseInt(row.registration_week, 10) * 1000);
  const windowStart = weekTimestamps.length > 0 ? new Date(Math.min(...weekTimestamps)) : new Date();
  const windowEnd = weekTimestamps.length > 0 ? new Date(Math.max(...weekTimestamps) + 7 * 24 * 60 * 60 * 1000) : new Date();

  // Web Downloads (Studio) - matching the segment week window
  const eventsInWindow = (analyticsEventsPerDay.data || []).filter(item => {
    const d = new Date(item.creation_date);
    return d >= windowStart && d <= windowEnd;
  });

  const firstProgramStartsInWindow = (firstProgramStarts.data || []).filter(item =>
    selectedWeekKeys.has(String(item.creation_week))
  );

  const studioProgramStarts = firstProgramStartsInWindow
    .reduce((sum, row) => sum + (parseInt(row.first_program_starts_studio, 10) || 0), 0);
  const songwriterProgramStarts = firstProgramStartsInWindow
    .reduce((sum, row) => sum + (parseInt(row.first_program_starts_songwriter, 10) || 0), 0);
  const studioMac = getDataSet(eventsInWindow, 'web_download_studio_mac');
  const studioWin = getDataSet(eventsInWindow, 'web_download_studio_windows');
  const webDownloads = studioMac.reduce((sum, v) => sum + parseInt(v || 0, 10), 0)
    + studioWin.reduce((sum, v) => sum + parseInt(v || 0, 10), 0);

  const sumField = (field) => windowedSegments.reduce((sum, row) => sum + (parseInt(row[field], 10) || 0), 0);

  const paid = sumField('paid_ever_users_studio');
  const multi = sumField('multiple_songs_users_studio');
  const finished = sumField('finished_users_studio');
  const started = sumField('started_users_studio');
  const remaining = sumField('remaining_users_studio');

  const totalUsersWithAnyEvent = paid + multi + finished + started + remaining;
  const usersDidAnything = paid + multi + finished + started;
  const usersPaid = paid;

  const funnelSteps = [
    { label: 'Web Downloads (Studio)', value: webDownloads },
    { label: 'Program starts (first time)', value: studioProgramStarts },
    { label: 'New User Accounts', value: totalUsersWithAnyEvent },
    { label: 'Users that did anything', value: usersDidAnything },
    { label: 'Users who paid', value: usersPaid },
  ];

  const songwriterPaid = sumField('paid_ever_users_songwriter');
  const songwriterMulti = sumField('multiple_songs_users_songwriter');
  const songwriterFinished = sumField('finished_users_songwriter');
  const songwriterStarted = sumField('started_users_songwriter');
  const songwriterRemaining = sumField('remaining_users_songwriter');

  const songwriterProgramStarters = songwriterPaid + songwriterMulti + songwriterFinished + songwriterStarted + songwriterRemaining;
  const songwriterDidAnything = songwriterPaid + songwriterMulti + songwriterFinished + songwriterStarted;
  const songwriterEventsInWindow = eventsInWindow;
  const songwriterMac = getDataSet(songwriterEventsInWindow, 'web_download_songwriter_mac');
  const songwriterWin = getDataSet(songwriterEventsInWindow, 'web_download_songwriter_windows');
  const songwriterWebDownloads = songwriterMac.reduce((sum, v) => sum + parseInt(v || 0, 10), 0)
    + songwriterWin.reduce((sum, v) => sum + parseInt(v || 0, 10), 0);

  const songwriterFunnelSteps = [
    { label: 'Web Downloads (Songwriter)', value: songwriterWebDownloads },
    { label: 'Program starts (first time)', value: songwriterProgramStarts },
    { label: 'New User Accounts', value: songwriterProgramStarters },
    { label: 'Users that did anything', value: songwriterDidAnything },
    { label: 'Users who paid', value: songwriterPaid },
  ];

  const topValue = funnelSteps[0].value || 1;
  const songwriterTopValue = songwriterFunnelSteps[0].value || 1;

  const stepColors = [colors.primary, colors.teal, colors.yellow, colors.secondary];

  const error = errors.analyticsEventsPerDay || segError || startsError;

  const renderFunnelBars = (steps, currentTopValue) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0, alignItems: 'flex-start', padding: '24px 0' }}>
      {steps.map((step, i) => {
        const pctOfTop = currentTopValue > 0 ? (step.value / currentTopValue) * 100 : 0;
        const barWidth = Math.max(0, Math.min(100, pctOfTop));
        const labelInside = barWidth >= 22;
        const stepAliases = ['downloads', 'program starts', 'new accounts', 'did anything', 'paid'];
        const comparisonText = i === 0
          ? `${pctOfTop.toFixed(1)}%`
          : Array.from({ length: i }, (_, prevIndex) => {
            const baseValue = steps[prevIndex].value;
            const pct = baseValue > 0 ? (step.value / baseValue) * 100 : 0;
            return `${pct.toFixed(1)}% of ${stepAliases[prevIndex]}`;
          }).join(' | ');
        return (
          <div key={step.label} style={{ width: '100%', maxWidth: 780, marginBottom: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 6 }}>
              <span style={{ fontWeight: 600, fontSize: 14 }}>{step.label}</span>
              <span style={{ fontSize: 13, color: '#7b809a' }}>
                {comparisonText}
              </span>
            </div>
            <div style={{
              background: '#f0f2f5',
              borderRadius: i === 0 ? '8px 8px 0 0' : i === steps.length - 1 ? '0 0 8px 8px' : 0,
              overflow: 'hidden',
              height: 48,
              display: 'flex',
              alignItems: 'center',
              position: 'relative',
            }}>
              <div style={{
                width: `${barWidth}%`,
                height: '100%',
                background: stepColors[i % stepColors.length],
                borderRadius: 'inherit',
                transition: 'width 0.5s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                {labelInside && (
                  <span style={{ color: '#fff', fontWeight: 700, fontSize: 18, whiteSpace: 'nowrap', padding: '0 8px' }}>
                    {step.value.toLocaleString()}
                  </span>
                )}
              </div>
              {!labelInside && (
                <span
                  style={{
                    position: 'absolute',
                    left: `calc(${barWidth}% + 8px)`,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#344767',
                    fontWeight: 700,
                    fontSize: 18,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {step.value.toLocaleString()}
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <section>
      <div className="chart-grid">
        <div className="chart-col-full">
          <ChartCard
            header={`Studio Onboarding Funnel - Users with registration from ${windowStart.toISOString().slice(0, 10)} to ${windowEnd.toISOString().slice(0, 10)} (${WEEKS_TO_USE} weeks, skipping last ${WEEKS_TO_SKIP})`}
            loading={loading}
            error={error}
            description={`Web Downloads: Studio Mac + Win. Program starts: first start per client_id in the window. New User Accounts: New accounts with any event in Studio. Did anything: triggered at least a Starting event. Paid: users who had a paying subscription.`}
            hideAvg
            hideTotal
          >
            {renderFunnelBars(funnelSteps, topValue)}
          </ChartCard>
        </div>

        <div className="chart-col-full">
          <ChartCard
            header={`Songwriter Onboarding Funnel - Users with registration from ${windowStart.toISOString().slice(0, 10)} to ${windowEnd.toISOString().slice(0, 10)} (${WEEKS_TO_USE} weeks, skipping last ${WEEKS_TO_SKIP})`}
            loading={loading}
            error={error}
            description={`Web Downloads: Songwriter Mac + Win. Program starts: first start per client_id in the window. New User Accounts: new accounts with any event in Songwriter. Did anything: triggered at least a Starting event. Paid: users who had a paying subscription.`}
            hideAvg
            hideTotal
          >
            {renderFunnelBars(songwriterFunnelSteps, songwriterTopValue)}
          </ChartCard>
        </div>
      </div>
    </section>
  );
};

export default OnboardingFunnel;
